import React, { useContext, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, XCircle, Activity, FileText, ListTodo, LogIn, LogOut, UserCheck } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import cartoviewerService from '../../../../services/cartoviewerService';
import RecentHistoriesSection from './RecentHistoriesSection';

export default function CartoViewerDashboardHome() {
  const { authTokens } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentHistories, setRecentHistories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!authTokens?.access) {
        console.log('❌ No access token available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🚀 Starting cartoviewer dashboard data fetch...');

        const [statsResult, historiesResult] = await Promise.all([
          cartoviewerService.getCartoViewerDashboardStats(authTokens.access),
          cartoviewerService.getCartoViewerRecentHistory(authTokens.access)
        ]);

        if (statsResult.success) {
          setDashboardData(statsResult.data);
          console.log('✅ Dashboard stats loaded:', statsResult.data);
        } else {
          console.error('❌ Failed to load stats:', statsResult.error);
        }

        if (historiesResult.success) {
          setRecentHistories(historiesResult.data);
          console.log('✅ Recent histories loaded:', historiesResult.data);
        } else {
          console.error('❌ Failed to load histories:', historiesResult.error);
        }

        console.log('✅ All API data loaded successfully!');
      } catch (error) {
        console.error('❌ Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [authTokens?.access]);

  const getActionIcon = (actionType) => {
    switch (actionType?.toLowerCase()) {
      case 'login':
        return <LogIn className="w-4 h-4 text-green-600" />;
      case 'logout':
        return <LogOut className="w-4 h-4 text-gray-600" />;
      case 'update':
        return <Activity className="w-4 h-4 text-blue-600" />;
      case 'create':
        return <CheckCircle className="w-4 h-4 text-teal-600" />;
      case 'delete':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <UserCheck className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType?.toLowerCase()) {
      case 'login':
        return 'bg-green-50 border-green-200';
      case 'logout':
        return 'bg-gray-50 border-gray-200';
      case 'update':
        return 'bg-blue-50 border-blue-200';
      case 'create':
        return 'bg-teal-50 border-teal-200';
      case 'delete':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;
    if (diffInDays < 7) return `Il y a ${diffInDays}j`;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData || {};

  const statisticsCards = [
    {
      title: 'Processus Publiés',
      value: stats.published_processes || 0,
      icon: FileText,
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600'
    },
    {
      title: 'Tâches en Attente',
      value: stats.tasks_in_pending || 0,
      icon: Clock,
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-600'
    },
    {
      title: 'Tâches Acceptées',
      value: stats.tasks_in_accepted || 0,
      icon: CheckCircle,
      bgColor: 'bg-green-100',
      textColor: 'text-green-600'
    },
    {
      title: 'Tâches en Cours',
      value: stats.tasks_in_progress || 0,
      icon: Activity,
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-600'
    },
    {
      title: 'Tâches Terminées',
      value: stats.tasks_completed || 0,
      icon: CheckCircle,
      bgColor: 'bg-teal-100',
      textColor: 'text-teal-600'
    },
    {
      title: 'Tâches Rejetées',
      value: stats.tasks_in_rejected || 0,
      icon: XCircle,
      bgColor: 'bg-red-100',
      textColor: 'text-red-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Tableau de Bord CartoViewer
          </h1>
          <p className="text-gray-600">
            Vue d'ensemble de vos tâches et processus
          </p>
        </div>

        {/* Statistics Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {statisticsCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      {card.title}
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {card.value}
                    </p>
                  </div>
                  <div className={`${card.bgColor} p-3 rounded-lg`}>
                    <Icon className={`w-6 h-6 ${card.textColor}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Histories Section */}
        <RecentHistoriesSection data={recentHistories} loading={loading} />

        {/* Empty State for Dashboard Data */}
        {!dashboardData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center mt-8">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucune donnée disponible
            </h3>
            <p className="text-gray-600">
              Impossible de charger les statistiques du tableau de bord.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}